import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CForm,
  CFormInput,
  CButton,
  CModal,
  CModalTitle,
  CModalHeader,
  CModalBody,
  CFormLabel,
  CModalFooter,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CAlert,
  CSpinner,
} from "@coreui/react";
import { apiClient } from "services/Api";
import SelectInput from "components/forms/SelectInput";
import { triggerAlert } from "components/globals/AlertNotification";
import * as XLSX from "xlsx";

const NAME_STOPWORDS = new Set([
  "da",
  "de",
  "do",
  "das",
  "dos",
  "e",
  "d",
  "del",
  "di",
  "van",
  "von",
]);

const MIN_TECH_CONFIDENCE = 0.6;

const levenshteinDistance = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const matrix = Array.from({ length: a.length + 1 }, () =>
    new Array(b.length + 1).fill(0)
  );

  for (let i = 0; i <= a.length; i += 1) {
    matrix[i][0] = i;
  }

  for (let j = 0; j <= b.length; j += 1) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;

      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1, // deletion
        matrix[i][j - 1] + 1, // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }

  return matrix[a.length][b.length];
};

const PaymentExportModal = ({ clientId, projectId, onClose, setToast }) => {
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [templateFile, setTemplateFile] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [templateError, setTemplateError] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const [team, setTeam] = useState(null);
  const [activities, setActivities] = useState([]);

  const [rhAssignments, setRhAssignments] = useState([]);
  const [orderOptions, setOrderOptions] = useState([]);
  const [activitiesAssignments, setActivitiesAssignments] = useState([]);

  const [loadingTeam, setLoadingTeam] = useState(true);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [remunerationErrors, setRemunerationErrors] = useState([]);

  // Fetch project team members
  useEffect(() => {
    apiClient
      .get(`/projects/management/${clientId}/${projectId}/team_person/`)
      .then((response) => {
        setTeam(response.data);
      })
      .catch(() => {
        if (setToast) {
          setToast({
            title: "Equipa",
            message: "Não foi possível carregar a equipa.",
            type: "error",
          });
        }
      })
      .finally(() => {
        setLoadingTeam(false);
      });
  }, [clientId, projectId, setToast]);

  // Fetch project activities
  useEffect(() => {
    apiClient
      .get(`/projects/management/${clientId}/${projectId}/activities/`)
      .then((response) => {
        const items = Array.isArray(response.data?.activities)
          ? response.data.activities
          : [];

        setActivities(
          items
            .filter((i) => i.management.parent != null)
            .map((act) => ({
              id: String(act.id),
              name: act.name,
              orderNumber: act.management.order_number,
            }))
        );
      })
      .catch(() => {
        if (setToast) {
          setToast({
            title: "Atividades",
            message: "Não foi possível carregar as atividades do projeto.",
            type: "error",
          });
        }
      })
      .finally(() => setLoadingActivities(false));
  }, [clientId, projectId, setToast]);

  const teamOptions = useMemo(
    () =>
      (team ?? []).map((rh) => ({
        value: String(rh.id),
        label: rh.name,
      })),
    [team]
  );

  const activityOptions = useMemo(
    () =>
      activities.map((act) => ({
        value: String(act.id),
        label: act.number ? `${act.number} - ${act.name}` : act.name,
      })),
    [activities]
  );

  const normalizeName = useCallback((value) => {
    return String(value ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }, []);

  const tokenizeName = useCallback(
    (value) => {
      if (!value) return [];
      return normalizeName(value)
        .replace(/[-']/g, " ")
        .split(/\s+/)
        .filter((token) => token && isNaN(token));
    },
    [normalizeName]
  );

  const computeNameConfidence = useCallback(
    (excelName, teamName) => {
      if (!excelName || !teamName) return 0;

      const excelNorm = normalizeName(excelName);
      const teamNorm = normalizeName(teamName);

      if (!excelNorm || !teamNorm) return 0;
      if (excelNorm === teamNorm) return 1;

      const excelTokensArr = tokenizeName(excelName);
      const teamTokensArr = tokenizeName(teamName);

      const excelTokens = new Set(excelTokensArr);
      const teamTokens = new Set(teamTokensArr);

      let intersection = 0;
      let firstMatch = excelTokensArr[0] === teamTokensArr[0] ? 0.05 : 0;
      let lastMatch =
        excelTokensArr[excelTokensArr.length - 1] ===
        teamTokensArr[teamTokensArr.length - 1]
          ? 0.05
          : 0;

      teamTokens.forEach((token) => {
        if (excelTokens.has(token)) {
          intersection += 1;
        }
      });

      const coverageOnTeam =
        teamTokens.size > 0 ? intersection / teamTokens.size : 0;
      const coverageOnExcel =
        excelTokens.size > 0 ? intersection / excelTokens.size : 0;

      const levenshteinScore =
        1 -
        levenshteinDistance(excelNorm, teamNorm) /
          Math.max(excelNorm.length, teamNorm.length);

      const combined =
        0.7 * coverageOnTeam +
        0.2 * coverageOnExcel +
        0.1 * Math.max(0, levenshteinScore);

      return Math.max(coverageOnTeam, combined) * 0.9 + firstMatch + lastMatch;
    },
    [normalizeName, tokenizeName]
  );

  const teamMap = useMemo(() => {
    const map = new Map();
    (team ?? []).forEach((rh) => {
      map.set(normalizeName(rh.name), rh);
    });
    return map;
  }, [team, normalizeName]);

  const findBestTeamMatch = useCallback(
    (excelName) => {
      if (!excelName || !team || team.length === 0) {
        return { id: "", confidence: 0 };
      }

      const normalized = normalizeName(excelName);
      if (!normalized) {
        return { id: "", confidence: 0 };
      }

      const direct = teamMap.get(normalized);
      if (direct) {
        return { id: String(direct.id), confidence: 1 };
      }

      let best = { id: "", confidence: 0 };
      team.forEach((rh) => {
        const score = computeNameConfidence(excelName, rh.name);
        if (excelName === "35 - RICARDO FILIPE OLIVEIRA BARROS") {
          console.log("Comparing:", rh.name, "score:", score);
        }
        if (score > best.confidence) {
          best = { id: String(rh.id), confidence: score };
        }
      });

      return best.confidence >= MIN_TECH_CONFIDENCE
        ? best
        : { id: "", confidence: 0 };
    },
    [team, teamMap, normalizeName, computeNameConfidence]
  );

  const parseOrderNumber = (raw) => {
    if (raw == null) return "";
    const str = String(raw).trim();
    if (str === "") return "";

    const normalized = Number(str.replace(",", "."));
    if (!Number.isNaN(normalized)) {
      return Number.isInteger(normalized)
        ? String(normalized)
        : normalized.toString();
    }
    return str;
  };

  const findActivityIdByOrder = useCallback(
    (orderNumber) => {
      const normalizedExcel = String(orderNumber ?? "")
        .trim()
        .replace(/\s+/g, " ");

      if (!normalizedExcel) return "";

      const normalizedExcelLower = normalizedExcel.toLowerCase();
      const excelNumeric = Number(normalizedExcel.replace(",", "."));
      const hasNumericValue = !Number.isNaN(excelNumeric);

      const match = activities.find((act) => {
        const candidateRaw = act.number ?? "";
        const candidateNormalized = String(candidateRaw)
          .trim()
          .replace(/\s+/g, " ");

        if (!candidateNormalized) return false;

        const candidateNormalizedLower = candidateNormalized.toLowerCase();

        if (hasNumericValue) {
          const candidateNumeric = Number(
            candidateNormalized.replace(",", ".")
          );

          if (
            !Number.isNaN(candidateNumeric) &&
            candidateNumeric === excelNumeric
          ) {
            return true;
          }
        }

        return candidateNormalizedLower === normalizedExcelLower;
      });

      return match ? String(match.id) : "";
    },
    [activities]
  );

  const handleTemplateUpload = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const extension = file.name.split(".").pop()?.toLowerCase();
      if (!extension || !["xls", "xlsx"].includes(extension)) {
        setTemplateError("O ficheiro deve ser um Excel (.xls ou .xlsx).");
        setTemplateFile(null);
        setTemplateName("");
        setRhAssignments([]);
        setActivitiesAssignments([]);
        event.target.value = "";
        return;
      }

      setTemplateError(null);
      setTemplateFile(file);
      setTemplateName(file.name);
      setRemunerationErrors([]);
      setLoadingTemplate(true);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const buffer = e.target?.result;
          const workbook = XLSX.read(new Uint8Array(buffer), {
            type: "array",
          });

          const worksheet =
            workbook.Sheets["Referências"] ||
            workbook.Sheets["Referencias"] ||
            workbook.Sheets["REFERÊNCIAS"] ||
            workbook.Sheets["REFERENCIAS"];

          if (!worksheet) {
            throw new Error("O ficheiro não contém a sheet 'Referências'.");
          }

          const ref = worksheet["!ref"];
          if (!ref) {
            throw new Error("A sheet 'Referências' está vazia.");
          }

          const range = XLSX.utils.decode_range(ref);
          const firstRow = Math.max(range.s.r, 4); // começa na linha 5 (index 4)

          const techParsed = [];
          const ordOptions = [];

          for (let row = firstRow; row <= range.e.r; row += 1) {
            const technicianCell =
              worksheet[XLSX.utils.encode_cell({ r: row, c: 7 })]; // Coluna H
            const orderCell =
              worksheet[XLSX.utils.encode_cell({ r: row, c: 4 })]; // Coluna E

            const technicianName = technicianCell?.v
              ? String(technicianCell.v).trim()
              : "";
            const orderNumber = parseOrderNumber(orderCell?.v);

            if (orderNumber) {
              ordOptions.push({ value: orderNumber, label: orderNumber });
            }
            if (!technicianName) continue;

            const bestMatch = technicianName
              ? findBestTeamMatch(technicianName)
              : { id: "", confidence: 0 };

            techParsed.push({
              id: row + 1,
              technicianName,
              technicianId: bestMatch.id,
              technicianConfidence: bestMatch.id ? bestMatch.confidence : null,
            });
          }

          if (techParsed.length === 0 || ordOptions.length === 0) {
            throw new Error(
              "Não foram encontrados técnicos ou números de ordem a partir da linha 5."
            );
          }

          setRhAssignments(techParsed);
          setOrderOptions(ordOptions);
          setActivitiesAssignments(
            activities.map((act) => ({
              id: act.id,
              orderNumber: act.orderNumber,
              activity: act.name,
            }))
          );
        } catch (error) {
          console.error(error);
          setRhAssignments([]);
          setActivitiesAssignments([]);
          setOrderOptions([]);
          setTemplateError(
            error instanceof Error
              ? error.message
              : "Ocorreu um erro ao processar o ficheiro."
          );
        } finally {
          setLoadingTemplate(false);
          event.target.value = "";
        }
      };

      reader.onerror = () => {
        setTemplateError("Não foi possível ler o ficheiro selecionado.");
        setTemplateFile(null);
        setTemplateName("");
        setRhAssignments([]);
        setLoadingTemplate(false);
        event.target.value = "";
      };

      reader.readAsArrayBuffer(file);
    },
    [findActivityIdByOrder, findBestTeamMatch]
  );

  useEffect(() => {
    if (!rhAssignments.length || !team || team.length === 0) return;

    setRhAssignments((prev) => {
      let changed = false;
      const updated = prev.map((assignment) => {
        if (assignment.technicianId || !assignment.technicianName) {
          return assignment;
        }

        const match = findBestTeamMatch(assignment.technicianName);

        if (!match.id) {
          return assignment;
        }

        changed = true;
        return {
          ...assignment,
          technicianId: match.id,
          technicianConfidence: match.confidence,
        };
      });

      return changed ? updated : prev;
    });
  }, [rhAssignments, team, findBestTeamMatch]);

  const updateRhAssignment = useCallback((id, patch) => {
    setRhAssignments((prev) =>
      prev.map((assignment) =>
        assignment.id === id ? { ...assignment, ...patch } : assignment
      )
    );
  }, []);

  const updateActivitiesAssignment = useCallback((id, patch) => {
    setActivitiesAssignments((prev) =>
      prev.map((assignment) =>
        assignment.id === id ? { ...assignment, ...patch } : assignment
      )
    );
  }, []);

  const pendingRhAssignments = useMemo(
    () => rhAssignments.filter((assignment) => !assignment.technicianId).length,
    [rhAssignments]
  );
  const pendingActivitiesAssignments = useMemo(
    () =>
      activitiesAssignments.filter((assignment) => !assignment.orderNumber)
        .length,
    [activitiesAssignments]
  );

  const parseErrorResponse = async (error) => {
    const data = error?.response?.data;
    if (!data) return null;

    if (data instanceof Blob) {
      try {
        const text = await data.text();
        return text ? JSON.parse(text) : null;
      } catch (parseErr) {
        console.error(parseErr);
        return null;
      }
    }

    return data;
  };

  const handleGenerate = async () => {
    if (!templateFile) return;

    setSubmitting(true);
    setRemunerationErrors([]);

    const form = new FormData();
    form.append("start_date", startDate);
    form.append("end_date", endDate);

    // Opcão A: enviar os arrays como JSON (simples de parse no backend)
    form.append(
      "rh_assignments",
      JSON.stringify(
        rhAssignments.map((a) => ({
          technician_name: a.technicianName,
          technician_id: a.technicianId,
        }))
      )
    );

    form.append(
      "activities_assignments",
      JSON.stringify(
        activitiesAssignments.map((a) => ({
          activity_id: a.id,
          order_number: a.orderNumber,
        }))
      )
    );

    // O ficheiro TEM de ir como File/Bolb aqui:
    form.append("file", templateFile, templateFile.name);

    try {
      const response = await apiClient.post(
        `/projects/management/${clientId}/${projectId}/payment_sheet/`,
        form,
        {
          responseType: "blob",
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Pedido_de_Pagamento_Projeto_${projectId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erro ao gerar o ficheiro:", err);
      const payload = await parseErrorResponse(err);

      if (payload?.remuneration_errors) {
        setRemunerationErrors(payload.remuneration_errors);
      }

      setToast({
        title: "Exportação",
        message:
          payload?.error || "Não foi possível gerar o pedido de pagamento.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CModal onClose={onClose} size="lg" backdrop="static" scrollable visible>
      <CModalHeader>
        <CModalTitle id="StaticBackdropExampleLabel">
          Pedido de Pagamento
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        <CForm className="row g-3">
          <div className="mb-3 d-flex">
            <div className="col-6 pe-3">
              <CFormLabel htmlFor="projectName">Data de Início</CFormLabel>
              <CFormInput
                type="month"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Introduz o programa de financiamento"
              />
            </div>
            <div className="col-6 ps-2">
              <CFormLabel htmlFor="projectName">Data de Fim</CFormLabel>
              <CFormInput
                type="month"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="Introduz o código do projeto"
              />
            </div>
          </div>

          <div className="col-12 mb-3">
            <CFormLabel>Template</CFormLabel>
            <div className="d-flex align-items-center gap-3 mt-2">
              <CFormInput
                type="file"
                accept=".xls,.xlsx"
                onChange={handleTemplateUpload}
                disabled={loadingTemplate}
              />
              {loadingTemplate && <CSpinner size="sm" />}
            </div>

            {templateName && (
              <small className="text-muted d-block mt-2">{templateName}</small>
            )}

            {templateError && (
              <CAlert color="danger" className="mt-3">
                {templateError}
              </CAlert>
            )}
          </div>

          {rhAssignments.length > 0 && (
            <>
              <h6 className="mt-4 mb-3">Cruzamento de Técnicos</h6>
              <CTable hover responsive bordered>
                <CTableHead color="light">
                  <CTableRow>
                    <CTableHeaderCell scope="col">
                      Técnico (Excel)
                    </CTableHeaderCell>
                    <CTableHeaderCell scope="col">
                      Técnico (SAP)
                    </CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {rhAssignments.map((assignment) => {
                    const technicianMissing =
                      !!assignment.technicianName && !assignment.technicianId;

                    return (
                      <CTableRow key={assignment.id}>
                        <CTableDataCell>
                          <div className="d-flex flex-column gap-1 text-muted">
                            {assignment.technicianName || "—"}
                          </div>
                        </CTableDataCell>
                        <CTableDataCell>
                          <div className="d-flex flex-column gap-1">
                            <SelectInput
                              value={assignment.technicianId}
                              onChange={(value) =>
                                updateRhAssignment(assignment.id, {
                                  technicianId: value,
                                  technicianConfidence: null,
                                })
                              }
                              options={teamOptions}
                            />
                            {assignment.technicianId &&
                              assignment.technicianConfidence != null && (
                                <small className="text-muted">
                                  Sugestão automática (
                                  {Math.round(
                                    assignment.technicianConfidence * 100
                                  )}
                                  %)
                                </small>
                              )}
                            {technicianMissing && (
                              <small className="text-warning">
                                Técnico não encontrado na equipa do projeto.
                              </small>
                            )}
                          </div>
                        </CTableDataCell>
                      </CTableRow>
                    );
                  })}
                </CTableBody>
              </CTable>

              {pendingRhAssignments > 0 && (
                <CAlert color="warning">
                  Existem {pendingRhAssignments} registos sem técnico ou
                  atividade associados.
                </CAlert>
              )}
            </>
          )}

          {activitiesAssignments.length > 0 && (
            <>
              <h6 className="mt-4 mb-3">Cruzamento de Nº Ordem</h6>
              <CTable hover responsive bordered>
                <CTableHead color="light">
                  <CTableRow>
                    <CTableHeaderCell scope="col">
                      Atividade do Projeto
                    </CTableHeaderCell>
                    <CTableHeaderCell scope="col">
                      Nº Ordem (E)
                    </CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {activitiesAssignments.map((assignment) => {
                    return (
                      <CTableRow key={assignment.id}>
                        <CTableDataCell>
                          <div className="d-flex flex-column gap-1 text-muted">
                            {assignment.activity || "—"}
                          </div>
                        </CTableDataCell>
                        <CTableDataCell className="align-middle">
                          <SelectInput
                            value={assignment.orderNumber}
                            onChange={(value) =>
                              updateActivitiesAssignment(assignment.id, {
                                orderNumber: value,
                              })
                            }
                            options={orderOptions}
                          />
                        </CTableDataCell>
                      </CTableRow>
                    );
                  })}
                </CTableBody>
              </CTable>

              {pendingActivitiesAssignments > 0 && (
                <CAlert color="warning">
                  Existem {pendingActivitiesAssignments} registos sem numero de
                  ordem.
                </CAlert>
              )}
            </>
          )}

          {remunerationErrors.length > 0 && (
            <CAlert color="danger" className="mt-3">
              <div className="fw-bold mb-2">
                Foram encontradas diferenças nas remunerações:
              </div>
              <ul className="mb-0 ps-3">
                {remunerationErrors.map((error, idx) => (
                  <li key={`${error.technician_id || idx}-${idx}`}>
                    <div className="fw-semibold">
                      {error.technician_name || "Técnico sem nome"}
                    </div>
                    <ul className="mb-2 ps-3">
                      {(error.issues || []).map((issue, issueIdx) => (
                        <li key={`${error.technician_id || idx}-${issueIdx}`}>
                          {issue}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </CAlert>
          )}
        </CForm>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={onClose}>
          Cancelar
        </CButton>
        <CButton
          color="primary"
          onClick={handleGenerate}
          disabled={
            !templateFile ||
            !startDate ||
            !endDate ||
            pendingRhAssignments > 0 ||
            pendingActivitiesAssignments > 0 ||
            loadingTemplate ||
            loadingActivities ||
            loadingTeam ||
            submitting
          }
        >
          {submitting && <CSpinner size="sm" className="me-2" />}
          {submitting ? "A gerar..." : "Gerar Exportação"}
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default PaymentExportModal;
